import { DisplayLabel } from '@common/components/forms';
import { ProactivePrescriptionRuleModel } from '@common/models';
import { observer } from 'mobx-react';
import React from 'react';

interface IProactivePrescriptionRuleEligibilityRulesProps {
  rule: ProactivePrescriptionRuleModel;
}

interface CommonRulesProps {
  isFeline?: boolean;
  isFleaTick?: boolean;
  isCombo?: boolean;
  isHeartworm?: boolean;
}

const CommonRules = ({
  isFeline = false,
  isFleaTick = false,
  isCombo = false,
  isHeartworm = false,
}: CommonRulesProps) => (
  <>
    <li>
      The pet is at least{' '}
      <strong>{isFeline ? '6 months old' : '1 year old'}</strong> with birthdate
      recorded in PetPass.
    </li>
    <li>
      The pet meets the <strong>minimum and maximum weight requirement</strong>{' '}
      for the medication.
    </li>
    {(isFleaTick || isFeline) && (
      <li>
        The pet does not have
        {isFeline ? ' preventative ' : ' flea/tick '} protection recorded in the{' '}
        <strong>past 10 months</strong>.
      </li>
    )}
    {isFeline && (isCombo || isHeartworm) && (
      <li>
        A <strong>heartworm test</strong> is performed during the current visit.
      </li>
    )}
  </>
);

const HeartwormRules = () => (
  <li>
    A <strong>heartworm test</strong> is performed during the current visit.
  </li>
);

const ComboRules = () => (
  <>
    <li>The pet is eligible for both the flea/tick and heartworm medication</li>
    <li>
      A <strong>heartworm test</strong> is performed during the current visit.
    </li>
  </>
);

export const ProactivePrescriptionRuleEligibilityRules = observer(
  ({ rule }: IProactivePrescriptionRuleEligibilityRulesProps) => {
    if (!rule.name) return null;

    const lowerName = rule.name.toLowerCase();
    const isFeline = rule.speciesId === 2;
    const isFleaTick = lowerName.includes('flea');
    const isCombo = lowerName.includes('combo');
    const isHeartworm = lowerName.includes('heartworm') && !isCombo;

    const flags = { isFeline, isFleaTick, isCombo, isHeartworm };

    return (
      <section className="displayGroup d-flex flex-column gap-1">
        <DisplayLabel text="Eligibility Rules" />
        <p>
          At the time of Treatment Plan generation, if a pet is eligible, but
          does not include a preventative medication or prescription for a
          preventative medication, then PetPass will proactively insert the
          preventative medication listed in the attending DVMs preferences.
          Eligible pets are defined as follows:
        </p>
        <ul>
          <CommonRules {...flags} />
          {!isFeline && isCombo && <ComboRules />}
          {!isFeline && isHeartworm && <HeartwormRules />}
        </ul>
      </section>
    );
  },
);
